import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

import { GET_ACTIVE_RULES_QUERY_KEY } from "./useGetActiveRules";
import { LIST_RULE_VERSIONS_QUERY_KEY } from "./useListRuleVersions";
import type {
  ApiErrorEnvelope,
  PublishRuleGraphBody,
  RuleGraph,
} from "./types";

/**
 * Publishes a new version of the decision-rule graph.
 *
 * BE contract:
 * - Success returns the persisted `RuleGraph` with server-stamped
 *   `version`, `created_at`, `created_by` fields filled in.
 * - The request body is `{ source_node_id, nodes }` only. Do NOT
 *   forward server-stamped fields (`version`, `created_at`,
 *   `created_by`) or a `base_version` guard — the BE rejects
 *   unknown top-level keys with a 422. Each publish is
 *   unconditional and mints a fresh version stamp.
 * - 422 carries structured validation errors on
 *   `error.response.data.error.details.errors[]` — the UI keys off
 *   `code` + `node_id` to highlight the offending nodes. This hook
 *   does NOT unwrap those errors; it just propagates the axios error
 *   so callers can render them however they need.
 *
 * On success, invalidates BOTH:
 * - `GET_ACTIVE_RULES_QUERY_KEY` — the newly-published graph *is* the
 *   active one now.
 * - `LIST_RULE_VERSIONS_QUERY_KEY` — a fresh version was minted, and
 *   `is_current` flipped on the previously-active row. Without this,
 *   opening the versions drawer immediately after a publish would
 *   render a stale list for up to the query `staleTime` window.
 */
async function publishRules(
  input: PublishRuleGraphBody,
): Promise<RuleGraph> {
  const instance = getPrivateInstance();
  const response = await instance.post<{ data: RuleGraph }>(
    API_ROUTES.PUBLISH_WITHDRAWAL_RULES,
    input,
  );
  const published = response?.data?.data;
  if (!published) {
    // Defensive: BE contract says 2xx always carries `data`. If it
    // doesn't, treat as an error so the mutation surfaces `isError`
    // instead of silently resolving with `undefined`.
    throw new Error("Publish response missing `data` payload");
  }
  return published;
}

export type UsePublishRulesResult = UseMutationResult<
  RuleGraph,
  AxiosError<ApiErrorEnvelope>,
  PublishRuleGraphBody
>;

/**
 * React Query mutation for publishing a new rule-graph version.
 *
 * Callers pass a `PublishRuleGraphBody` — the raw graph shape with
 * `source_node_id` + `nodes`. No optimistic-concurrency guard is
 * sent; the BE dropped support for `base_version` in the publish
 * body, so every publish is an unconditional overwrite.
 *
 * The hook returns the standard React Query mutation object:
 *   - `mutate(body)` / `mutateAsync(body)` to trigger a POST
 *   - `isPending`, `isSuccess`, `isError`, `data`, `error`
 *   - `data` is the freshly-published `RuleGraph` with server fields
 *
 * Retries are disabled by default (React Query mutation default) —
 * republishing a "maybe-succeeded" request is dangerous because it
 * mints a duplicate version.
 */
export function usePublishRules(): UsePublishRulesResult {
  const queryClient = useQueryClient();
  return useMutation<
    RuleGraph,
    AxiosError<ApiErrorEnvelope>,
    PublishRuleGraphBody
  >({
    mutationFn: publishRules,
    onSuccess: () => {
      // Publishing mints a new version AND rotates the active pointer,
      // so both caches must go stale. Kept as invalidate-not-setData
      // on the active-rules key so subscribers with different `select`
      // transforms all get a fresh read; the versions list can only
      // be reconstructed with a fresh GET (the previous current row
      // needs `is_current: false` and the new one needs to appear).
      queryClient.invalidateQueries({ queryKey: GET_ACTIVE_RULES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: LIST_RULE_VERSIONS_QUERY_KEY,
      });
    },
  });
}

// Re-export the body type at the hook barrel so callers don't have
// to import from `./types` in addition to the hook itself.
export type { PublishRuleGraphBody };
